import GIF from 'gif.js';

export interface GifExportOptions {
    /** Delay per frame in milliseconds (default: 2000) */
    frameDuration?: number;
    /** Output quality 1-30, lower = better quality (default: 10) */
    quality?: number;
    /** Output width (default: canvas width) */
    width?: number;
    /** Output height (default: canvas height) */
    height?: number;
}

/**
 * Generates an animated GIF from an array of canvas elements.
 * Each canvas becomes one frame in the GIF slideshow.
 */
export function generateCardGif(
    canvases: HTMLCanvasElement[],
    options: GifExportOptions = {},
): Promise<Blob> {
    const {
        frameDuration = 2000,
        quality = 10,
        width,
        height,
    } = options;

    if (canvases.length === 0) {
        return Promise.reject(new Error('No canvases provided for GIF export'));
    }

    // Use the first canvas dimensions as default
    const gifWidth = width || canvases[0].width;
    const gifHeight = height || canvases[0].height;

    return new Promise((resolve, reject) => {
        const gif = new GIF({
            workers: 2,
            quality,
            width: gifWidth,
            height: gifHeight,
            workerScript: `${process.env.PUBLIC_URL || ''}/gif.worker.js`,
            repeat: 0, // loop forever
        });

        for (const canvas of canvases) {
            // Scale each canvas to the target GIF dimensions
            const frameCanvas = document.createElement('canvas');
            frameCanvas.width = gifWidth;
            frameCanvas.height = gifHeight;
            const ctx = frameCanvas.getContext('2d');
            if (!ctx) continue;

            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, gifWidth, gifHeight);
            ctx.drawImage(canvas, 0, 0, gifWidth, gifHeight);

            gif.addFrame(frameCanvas, { delay: frameDuration, copy: true });
        }

        gif.on('finished', (blob: Blob) => {
            resolve(blob);
        });

        gif.on('error', (err: Error) => {
            reject(err);
        });

        gif.render();
    });
}
